// 全班合併 Word 檔（看板卡 1005602；作法移植自國中 chaiwriting-j utils/mergeDocx.js，卡 1004914）
// 把 buildCorrectionResultBlob 產出的多份單人 docx 接成一份，每位學生從新的一頁開始。
// 前提：各份出自同一個底稿，styles／numbering／settings／頁首頁尾等共用部件相同，直接沿用第一份。

import JSZip from "jszip";

export const MERGED_DOCX_FILENAME = "00_合併檔.docx";

const W_NS = "http://schemas.openxmlformats.org/wordprocessingml/2006/main";
const W14_NS = "http://schemas.microsoft.com/office/word/2010/wordml";
const R_NS = "http://schemas.openxmlformats.org/officeDocument/2006/relationships";
const REL_NS = "http://schemas.openxmlformats.org/package/2006/relationships";
const CONTENT_TYPES_NS = "http://schemas.openxmlformats.org/package/2006/content-types";
const WP_NS = "http://schemas.openxmlformats.org/drawingml/2006/wordprocessingDrawing";
const PIC_NS = "http://schemas.openxmlformats.org/drawingml/2006/picture";
const IMAGE_REL_TYPE = "http://schemas.openxmlformats.org/officeDocument/2006/relationships/image";

const DOCUMENT_PATH = "word/document.xml";
const RELS_PATH = "word/_rels/document.xml.rels";
const CONTENT_TYPES_PATH = "[Content_Types].xml";

const parseXml = (text: string): Document =>
  new window.DOMParser().parseFromString(text, "application/xml");
const serializeXml = (doc: Document): string => new window.XMLSerializer().serializeToString(doc);
const elementChildren = (node: Node): Element[] =>
  Array.from(node.childNodes).filter((n): n is Element => n.nodeType === 1);
const isW = (el: Element, localName: string): boolean =>
  el.namespaceURI === W_NS && el.localName === localName;
const descendantsAndSelf = (root: Element): Element[] => [
  root,
  ...Array.from(root.getElementsByTagName("*")),
];

export interface MergeItem {
  seat: string | number | null | undefined;
  blob: Blob;
}

// 座號空白、0 或不是數字的排最後；同座號維持原順序
const seatKey = (seat: MergeItem["seat"]): number => {
  const n = Number(seat);
  return seat !== "" && seat != null && Number.isFinite(n) && n > 0 ? n : Infinity;
};

export const sortBySeat = <T extends { seat: MergeItem["seat"] }>(items: T[]): T[] =>
  items
    .map((item, index) => ({ item, index, key: seatKey(item.seat) }))
    .sort((a, b) => (a.key === b.key ? a.index - b.index : a.key < b.key ? -1 : 1))
    .map(({ item }) => item);

// 每位學生之間的分隔段落：只放一個分頁符號，讓下一位從新的一頁開始。
// 不用 pageBreakBefore：Pages 遇到它會在每位學生之間都多排一張空白頁（Word、LibreOffice 不會）。
// 取捨：分頁符號所在的段落若被擠到下一頁，會多出一張空白頁。所以把前一位結尾的空段落（約 12pt 高）
// 換成這個行高 1pt 的段落，只有前一位最後一頁剩不到 1pt 時才會發生。
// 兩張表之間也一定要隔一個段落，Word 才不會把它們併成一張。
const createTinyRunProps = (doc: Document): Element => {
  const rPr = doc.createElementNS(W_NS, "w:rPr");
  for (const tag of ["w:sz", "w:szCs"]) {
    const size = doc.createElementNS(W_NS, tag);
    size.setAttributeNS(W_NS, "w:val", "2");
    rPr.appendChild(size);
  }
  return rPr;
};

const createSeparator = (doc: Document): Element => {
  const p = doc.createElementNS(W_NS, "w:p");
  const pPr = doc.createElementNS(W_NS, "w:pPr");
  const spacing = doc.createElementNS(W_NS, "w:spacing");
  spacing.setAttributeNS(W_NS, "w:before", "0");
  spacing.setAttributeNS(W_NS, "w:after", "0");
  spacing.setAttributeNS(W_NS, "w:line", "20");
  spacing.setAttributeNS(W_NS, "w:lineRule", "exact");
  pPr.appendChild(spacing);
  pPr.appendChild(createTinyRunProps(doc));
  p.appendChild(pPr);
  const r = doc.createElementNS(W_NS, "w:r");
  r.appendChild(createTinyRunProps(doc));
  const br = doc.createElementNS(W_NS, "w:br");
  br.setAttributeNS(W_NS, "w:type", "page");
  r.appendChild(br);
  p.appendChild(r);
  return p;
};

// 沒有任何內容、只有段落格式的段落（底稿結尾那個空段落）
const isEmptyParagraph = (el: Element): boolean =>
  isW(el, "p") && elementChildren(el).every((child) => isW(child, "pPr"));

// _GoBack 是 Word 記「上次編輯位置」的書籤，每份都有且 id 相同，合併後會重複，直接拿掉
const removeGoBackBookmarks = (body: Element): void => {
  const starts = Array.from(body.getElementsByTagNameNS(W_NS, "bookmarkStart")).filter(
    (el) => el.getAttributeNS(W_NS, "name") === "_GoBack"
  );
  const ids = new Set(starts.map((el) => el.getAttributeNS(W_NS, "id")));
  starts.forEach((el) => el.parentNode?.removeChild(el));
  Array.from(body.getElementsByTagNameNS(W_NS, "bookmarkEnd"))
    .filter((el) => ids.has(el.getAttributeNS(W_NS, "id")))
    .forEach((el) => el.parentNode?.removeChild(el));
};

// 新版 Word 存檔會在段落上加 w14:paraId／textId，每份都一樣，合併後會重複；拿掉後 Word 開檔時自己補
const removeParaIds = (el: Element): void => {
  el.removeAttributeNS(W14_NS, "paraId");
  el.removeAttributeNS(W14_NS, "textId");
};

// rels 的 Target 相對於 word/，也可能是以 / 開頭的套件絕對路徑
const partPath = (target: string): string =>
  target.startsWith("/") ? target.slice(1) : `word/${target}`;

const extensionOf = (target: string): string => {
  const match = target.match(/\.([A-Za-z0-9]+)$/);
  return match ? match[1].toLowerCase() : "bin";
};

const loadZip = async (blob: Blob): Promise<JSZip> =>
  JSZip.loadAsync(
    typeof blob.arrayBuffer === "function" ? new Uint8Array(await blob.arrayBuffer()) : blob
  );

const readXml = async (zip: JSZip, filePath: string): Promise<Document> => {
  const file = zip.file(filePath);
  if (!file) throw new Error(`docx 缺少 ${filePath}`);
  return parseXml(await file.async("text"));
};

const relationshipsOf = (relsDoc: Document): Map<string, Element> =>
  new Map(
    Array.from(relsDoc.getElementsByTagNameNS(REL_NS, "Relationship")).map((rel) => [
      rel.getAttribute("Id") ?? "",
      rel,
    ])
  );

/**
 * 把依合併順序排好的單人 docx 接成一份。
 */
export const mergeDocxBlobs = async (blobs: Blob[]): Promise<Blob> => {
  if (!blobs.length) throw new Error("沒有可合併的檔案");

  // 以第一份為底：共用部件與 sectPr（含頁首頁尾參照）沿用，body 內容與照片重新組
  const out = await loadZip(blobs[0]);
  const outDoc = await readXml(out, DOCUMENT_PATH);
  const outRels = await readXml(out, RELS_PATH);
  const outBody = outDoc.getElementsByTagNameNS(W_NS, "body")[0];
  if (!outBody) throw new Error("docx 缺少 body");
  const outSectPr = elementChildren(outBody).filter((el) => isW(el, "sectPr")).pop();
  if (!outSectPr) throw new Error("docx 缺少 sectPr");

  // 照片副檔名要有 Content Type 登記。高中底稿本身沒登記任何圖片類型，單人檔只在「這份有照片」時
  // 才登記它用到的那一種（jpg／png／gif／webp），所以第一份沒有的副檔名要從來源那份補過來
  const outContentTypes = await readXml(out, CONTENT_TYPES_PATH);
  const defaultsOf = (ctDoc: Document): Map<string, string> =>
    new Map(
      Array.from(ctDoc.getElementsByTagNameNS(CONTENT_TYPES_NS, "Default")).map((el) => [
        (el.getAttribute("Extension") ?? "").toLowerCase(),
        el.getAttribute("ContentType") ?? "",
      ])
    );
  const knownExtensions = new Set(defaultsOf(outContentTypes).keys());
  let contentTypesChanged = false;
  const registerExtension = async (ext: string, sourceZip: JSZip): Promise<void> => {
    if (knownExtensions.has(ext)) return;
    const contentType = defaultsOf(await readXml(sourceZip, CONTENT_TYPES_PATH)).get(ext);
    if (!contentType) throw new Error(`沒有 ${ext} 的 Content Type`);
    const def = outContentTypes.createElementNS(CONTENT_TYPES_NS, "Default");
    def.setAttribute("Extension", ext);
    def.setAttribute("ContentType", contentType);
    outContentTypes.documentElement.appendChild(def);
    knownExtensions.add(ext);
    contentTypesChanged = true;
  };
  const usedRelIds = new Set(relationshipsOf(outRels).keys());
  let relSeq = 0;
  let mediaSeq = 0;
  const newRelId = (): string => {
    let id: string;
    do id = `rIdM${++relSeq}`;
    while (usedRelIds.has(id));
    usedRelIds.add(id);
    return id;
  };

  // 照片複製成全檔唯一的檔名、建新的 rId。單人檔的 body 只會引用照片，
  // 其他關係（超連結、圖表、內嵌物件…）不支援，丟錯走「合併檔未產生」
  const copyImage = async (rel: Element, sourceZip: JSZip): Promise<string> => {
    if (rel.getAttribute("Type") !== IMAGE_REL_TYPE || rel.getAttribute("TargetMode") === "External") {
      throw new Error(`不支援合併的關係 ${rel.getAttribute("Type")} ${rel.getAttribute("Target")}`);
    }
    const sourcePath = partPath(rel.getAttribute("Target") ?? "");
    const bytes = await sourceZip.file(sourcePath)?.async("uint8array");
    if (!bytes) throw new Error(`docx 缺少 ${sourcePath}`);
    const ext = extensionOf(sourcePath);
    await registerExtension(ext, sourceZip);
    const target = `media/merged${++mediaSeq}.${ext}`;
    // 照片本身已是壓縮格式，不再 DEFLATE（同 downloadResultsUtil）
    out.file(`word/${target}`, bytes, { compression: "STORE" });
    const newRel = outRels.createElementNS(REL_NS, "Relationship");
    const id = newRelId();
    newRel.setAttribute("Id", id);
    newRel.setAttribute("Type", IMAGE_REL_TYPE);
    newRel.setAttribute("Target", target);
    outRels.documentElement.appendChild(newRel);
    return id;
  };

  // 取出一份的 body 內容（不含 sectPr），裡面的 r:embed 等參照改成全檔唯一
  const takeBodyContent = async (
    doc: Document,
    relsDoc: Document,
    sourceZip: JSZip
  ): Promise<{ children: Element[]; replacedRels: Element[] }> => {
    const body = doc.getElementsByTagNameNS(W_NS, "body")[0];
    if (!body) throw new Error("docx 缺少 body");
    removeGoBackBookmarks(body);
    const relById = relationshipsOf(relsDoc);
    const remap = new Map<string, string>();
    const children = elementChildren(body).filter((el) => !isW(el, "sectPr"));
    for (const el of children.flatMap(descendantsAndSelf)) {
      removeParaIds(el);
      for (const attr of Array.from(el.attributes)) {
        if (attr.namespaceURI !== R_NS) continue;
        const oldId = attr.value;
        if (!remap.has(oldId)) {
          const rel = relById.get(oldId);
          if (!rel) throw new Error(`找不到關係 ${oldId}`);
          remap.set(oldId, await copyImage(rel, sourceZip));
        }
        el.setAttributeNS(R_NS, attr.name, remap.get(oldId)!);
      }
    }
    return {
      children,
      replacedRels: Array.from(remap.keys()).map((id) => relById.get(id)!),
    };
  };

  // 第一份：照片複製成新名字後，原本的照片與關係移除
  const first = await takeBodyContent(outDoc, outRels, out);
  for (const rel of first.replacedRels) {
    out.remove(partPath(rel.getAttribute("Target") ?? ""));
    rel.parentNode?.removeChild(rel);
  }
  const parts: Element[][] = [first.children];

  // 其他份逐一載入、處理完就放掉，不同時把全班的檔案都留在記憶體裡
  for (let k = 1; k < blobs.length; k++) {
    const zip = await loadZip(blobs[k]);
    const { children } = await takeBodyContent(
      await readXml(zip, DOCUMENT_PATH),
      await readXml(zip, RELS_PATH),
      zip
    );
    parts.push(children.map((el) => outDoc.importNode(el, true)));
  }

  // 組 body：依序放入各份內容，每位學生之間插分隔段落，最後放回底稿的 sectPr。
  // 前一位結尾的空段落全部拿掉（高中底稿結尾有兩個），剛好滿頁時它們會被擠到下一頁，造成空白頁
  elementChildren(outBody).forEach((el) => outBody.removeChild(el));
  parts.forEach((children, k) => {
    const isLast = k === parts.length - 1;
    let end = children.length;
    if (!isLast) {
      while (end > 0 && isEmptyParagraph(children[end - 1])) end--;
    }
    if (k > 0) outBody.appendChild(createSeparator(outDoc));
    children.slice(0, end).forEach((el) => outBody.appendChild(el));
  });
  outBody.appendChild(outSectPr);

  // 圖片的 docPr／pic:cNvPr id 每份都從 1 開始，改成全檔唯一
  Array.from(outBody.getElementsByTagNameNS(WP_NS, "docPr")).forEach((el, i) =>
    el.setAttribute("id", String(i + 1))
  );
  Array.from(outBody.getElementsByTagNameNS(PIC_NS, "cNvPr")).forEach((el, i) =>
    el.setAttribute("id", String(i + 1))
  );

  out.file(DOCUMENT_PATH, serializeXml(outDoc));
  out.file(RELS_PATH, serializeXml(outRels));
  if (contentTypesChanged) out.file(CONTENT_TYPES_PATH, serializeXml(outContentTypes));

  return out.generateAsync({
    type: "blob",
    compression: "DEFLATE",
    mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  });
};

/**
 * 依座號排序、合併後以 00_合併檔.docx 放進 zip（或 zip 內的資料夾）。
 * 合併失敗不丟錯，zip 維持原樣，回傳 false。
 */
export const addMergedDocx = async (zip: JSZip, items: MergeItem[]): Promise<boolean> => {
  try {
    const merged = await mergeDocxBlobs(sortBySeat(items).map((item) => item.blob));
    zip.file(MERGED_DOCX_FILENAME, merged);
    return true;
  } catch (err) {
    console.error("全班合併檔產生失敗:", err);
    return false;
  }
};
